import { isSuperAdmin } from "@/helpers/UserHelpers";
import ErrorComponent from "@/components/atoms/ErrorComponent";
import Loader from "@/components/atoms/Loader";
import Select, { SelectPaginateAsync } from "@/components/atoms/Select";
import useLoadOptions from "@/hooks/useLoadOptions";
import { EMAIL_REGEX, PASSWORD_REGEX } from "@/constants/regexs";
import {
  INVALID_EMAIL2_MESSAGE,
  INVALID_EMAIL_MESSAGE,
  INVALID_FIRSTNAME_MESSAGE,
  INVALID_FORM_MESSAGE,
  INVALID_LASTNAME_MESSAGE,
  INVALID_MAXIMUM_CARACTERE_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
  INVALID_PHONE2_MESSAGE,
} from "@/constants/validatorMessages";
import getDifferenceBetweenTwoObjects, {
  matchPassword,
} from "@/helpers/FormHelpers";
import { CardEditProps } from "@/interfaces/Card";
import User, { USER_ROLES, UserUpdateForm } from "@/interfaces/IUser";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { useGetUserQuery, useUpdateUserMutation } from "@/services/users";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { isValidPhoneNumber } from "libphonenumber-js";
import { FC, useEffect } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Image,
  InputGroup,
  Row,
} from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useSelector } from "react-redux";
import { ToastContentProps, toast } from "react-toastify";
import CardHeader from "../../card-headers/CardHeader";
import { useNavigate } from "react-router-dom";

const UserEditCard: FC<CardEditProps<User>> = ({ id }) => {
  const navigation = useNavigate();
  const { data, isLoading, isError, error } = useGetUserQuery(id);
  const user = useSelector(selectCurrentUser);
  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<UserUpdateForm>();

  const [updateUserAsync] = useUpdateUserMutation();
  const { loadOrganisationsOptions } = useLoadOptions();

  useEffect(() => {
    data &&
      reset({
        ...data,
        avatar: null,
        signatureFile: null,
        roles: data.roles.map((role) => ({
          value: role,
          label: role,
        })),
        organisation: data.organisation ?? null,
      });
  }, [data]);

  const onSubmit: SubmitHandler<UserUpdateForm> = ({
    roles,
    organisation,
    ...newData
  }) => {
    const result = getDifferenceBetweenTwoObjects<UserUpdateForm>(data, {
      ...newData,
      organisationId: organisation?.id ?? null,
    });
    toast.promise(
      updateUserAsync([
        id,
        {
          ...result,
          roles: roles?.map((role) => role.value),
        },
      ]).unwrap(),
      {
        pending: "Modification de l'utilisateur en cours ...",
        success: {
          render() {
            navigation(-1);
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                L'utilisateur à bien été modifié 🤩
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }

            return "Une erreur est survenue";
          },
        },
      },
    );
  };

  const onError: SubmitErrorHandler<UserUpdateForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  if (isLoading) {
    return <Loader absolute />;
  } else if (isError) {
    return <ErrorComponent error={error} absolute returnButton />;
  }

  if (isSuperAdmin(data) && !isSuperAdmin(user))
    return <p role="status">Seul un SUPERADMIN peut modifier ce compte.</p>;

  return (
    <Form onSubmit={handleSubmit(onSubmit, onError)}>
      <Card>
        <CardHeader title="Modification de l'utilisateur" />
        <Card.Body className="bg-light">
          <Row className="gx-3">
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Avatar :</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  {...register("avatar")}
                  isInvalid={!!errors.avatar}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.avatar?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Signature électronique :</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  {...register("signatureFile")}
                  isInvalid={!!errors.signatureFile}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.signatureFile?.message}
                </Form.Control.Feedback>
                {data?.signatureFile?.link && (
                  <div className="mt-2">
                    <Form.Text className="text-muted d-block mb-1">
                      Signature actuelle :
                    </Form.Text>
                    <Image
                      fluid
                      className="rounded-3"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "100px",
                        objectFit: "contain",
                      }}
                      src={data.signatureFile.link}
                      alt="signature actuelle"
                    />
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Prénom <span className="text-danger">*</span> :
                </Form.Label>
                <Form.Control
                  placeholder="ex : Remi"
                  type="text"
                  {...register("firstname", {
                    required: {
                      value: true,
                      message: INVALID_FIRSTNAME_MESSAGE,
                    },
                    minLength: {
                      value: 2,
                      message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                    },
                    maxLength: {
                      value: 255,
                      message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                    },
                  })}
                  isInvalid={!!errors.firstname}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.firstname?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Nom de famille <span className="text-danger">*</span> :
                </Form.Label>
                <Form.Control
                  placeholder="ex : Dupont"
                  type="text"
                  {...register("lastname", {
                    required: {
                      value: true,
                      message: INVALID_LASTNAME_MESSAGE,
                    },
                    minLength: {
                      value: 2,
                      message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                    },
                    maxLength: {
                      value: 255,
                      message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                    },
                  })}
                  isInvalid={!!errors.lastname}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.lastname?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Email <span className="text-danger">*</span> :
                </Form.Label>
                <Form.Control
                  placeholder="ex : remi@groupe-pi.com"
                  type="email"
                  {...register("email", {
                    required: INVALID_EMAIL_MESSAGE,
                    pattern: {
                      value: EMAIL_REGEX,
                      message: INVALID_EMAIL2_MESSAGE,
                    },
                  })}
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Numéro de téléphone :</Form.Label>
                <Form.Control
                  placeholder="ex : +33 6 34 83 55 32"
                  type="text"
                  {...register("phone", {
                    validate: (value) => {
                      if (value && !isValidPhoneNumber(value, "FR")) {
                        return INVALID_PHONE2_MESSAGE;
                      }
                    },
                  })}
                  isInvalid={!!errors.phone}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.phone?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Roles <span className="text-danger">*</span> :
                </Form.Label>
                <Controller
                  control={control}
                  name="roles"
                  rules={{
                    required: "Vous devez choisir au moins un rôle",
                  }}
                  render={({ field, fieldState }) => (
                    <>
                      <Select
                        isDisabled={user?.id === id}
                        options={USER_ROLES.filter(
                          (role) =>
                            role.value !== "SUPERADMIN" || isSuperAdmin(user),
                        )}
                        value={field.value}
                        isMulti
                        closeMenuOnSelect
                        onChange={field.onChange}
                      />
                      {Boolean(fieldState.error) ? (
                        <h6 className="mb-0 mt-0 mt-1 text-danger">
                          {fieldState.error!.message}
                        </h6>
                      ) : null}
                    </>
                  )}
                />
                {user?.id === id && (
                  <Form.Text>
                    Vous ne pouvez pas modifier votre propre rôle.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Organisme :</Form.Label>
                <Controller
                  control={control}
                  name="organisation"
                  render={({ field }) => (
                    <SelectPaginateAsync
                      loadOptions={loadOrganisationsOptions}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id.toString()}
                      value={field.value}
                      isClearable
                      onChange={field.onChange}
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Commission du Head Of Sales :</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    {...register("headOfSalesCommission")}
                    placeholder="1"
                    step={0.1}
                    isInvalid={!!errors.headOfSalesCommission}
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {errors.headOfSalesCommission?.message}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3 me-auto">
                <Form.Label>Passer le compte actif :</Form.Label>
                <Form.Check
                  type="switch"
                  {...register("isActive")}
                  isInvalid={!!errors.isActive}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.isActive?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3 me-auto">
                <Form.Label>Est un ancien :</Form.Label>
                <Form.Check
                  type="switch"
                  {...register("isVeteran")}
                  isInvalid={!!errors.isVeteran}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.isVeteran?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {user?.id.toString() === id && (
              <>
                <hr />
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Mot de passe actuel</Form.Label> :
                    <Form.Control
                      placeholder="ex : 9rfDJde7d"
                      type="password"
                      {...register("oldPassword", {})}
                      isInvalid={!!errors.oldPassword}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.oldPassword?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nouveau mot de passe</Form.Label> :
                    <Form.Control
                      placeholder="ex : 8ddn2kKDB"
                      type="password"
                      {...register("newPassword", {
                        pattern: {
                          value: PASSWORD_REGEX,
                          message: "Votre mot de passe est invalide",
                        },
                      })}
                      isInvalid={!!errors.newPassword}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.newPassword?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Confirmation du nouveau mot de passe :
                    </Form.Label>
                    <Form.Control
                      placeholder="ex : 8ddn2kKDB"
                      type="password"
                      {...register("newPasswordConfirmation", {
                        pattern: {
                          value: PASSWORD_REGEX,
                          message: "Votre mot de passe est invalide",
                        },
                        validate: (value) =>
                          matchPassword(value, watch("newPassword")) ||
                          "Les mots de passe ne correspondent pas",
                      })}
                      isInvalid={!!errors.newPasswordConfirmation}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.newPasswordConfirmation?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </>
            )}
          </Row>
          <Button
            type="submit"
            variant="success"
            disabled={isLoading}
            className="mt-3"
          >
            Confirmer
          </Button>
        </Card.Body>
      </Card>
    </Form>
  );
};

export default UserEditCard;
